package com.bulut.mecitogullari.core.export

import com.bulut.mecitogullari.core.i18n.APP_NAME
import com.bulut.mecitogullari.core.tenant.CompanyProfile
import com.bulut.mecitogullari.core.util.formatAmount
import kotlin.math.roundToLong

/**
 * Shared skeleton and helpers for the printable PDF reports
 * (usta / proje / aylık işlemler). Same look as the debt statement:
 * navy table headers, green credits, red debits. The page auto-opens
 * the print dialog, where the user saves it as PDF.
 */
internal object ReportHtml {

    private const val STYLE = """
  body { font-family: Arial, Helvetica, sans-serif; color: #0F172A; margin: 32px; }
  h1 { font-size: 20px; margin: 0; }
  h2 { font-size: 14px; margin: 22px 0 8px 0; }
  .subtitle { color: #64748B; font-size: 12px; margin: 4px 0 16px 0; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th { text-align: left; background: #1E293B; color: #fff; padding: 7px 8px; }
  td { padding: 6px 8px; border-bottom: 1px solid #E2E8F0; }
  .num { text-align: right; white-space: nowrap; }
  .debit { color: #DC2626; }
  .credit { color: #16A34A; }
  .muted { color: #64748B; }
  tr.total td { font-weight: bold; border-top: 2px solid #0F172A; }
  .summary { margin-top: 14px; width: 360px; margin-left: auto; font-size: 13px; }
  .page-break { page-break-before: always; }
  .letterhead {
    display: flex; justify-content: space-between; align-items: baseline;
    border-bottom: 2px solid #0F172A; padding-bottom: 8px; margin-bottom: 18px;
  }
  .letterhead .company { font-size: 15px; font-weight: bold; }
  .letterhead .app { font-size: 11px; color: #64748B; letter-spacing: 2px; }
  @media print { body { margin: 12mm; } }
"""

    /** Tenant letterhead: the user's own company name, never a built-in one. */
    private fun letterhead(): String {
        val company = CompanyProfile.name.value.trim()
        return "<div class=\"letterhead\">" +
            "<span class=\"company\">${escape(company)}</span>" +
            "<span class=\"app\">$APP_NAME</span>" +
            "</div>"
    }

    fun page(title: String, body: String): String = """
<!DOCTYPE html>
<html lang="tr">
<head>
<meta charset="utf-8">
<title>${escape(title)}</title>
<style>$STYLE</style>
</head>
<body>
${letterhead()}
$body
<script>window.onload = function () { window.print(); };</script>
</body>
</html>
"""

    fun money(amount: Double): String = formatAmount(amount)

    /** 3.0 → "3", 2.5 → "2,5" — daire/iş adetleri. */
    fun count(value: Double): String =
        if (value == value.roundToLong().toDouble()) {
            value.roundToLong().toString()
        } else {
            value.toString().replace('.', ',')
        }

    fun escape(text: String): String = text
        .replace("&", "&amp;")
        .replace("<", "&lt;")
        .replace(">", "&gt;")
}

/** "Elektrikçi Ahmet" → "elektrikçi-ahmet" — safe piece of a pdf file name. */
fun safePdfName(raw: String): String = raw.lowercase()
    .map { if (it.isLetterOrDigit()) it else '-' }
    .joinToString("")
    .trim('-')
    .ifBlank { "rapor" }
